/**
 * VÉRAULT Portal — Experience Dossier (Phase 1.2)
 *
 * The Dossier tab inside a Trip Profile. Renders the unified view from
 * GET /api/dossier/trip/{trip_id}: summary header, missing-records strip,
 * three clear sections (Forms · Agreements · Documents) with counts,
 * inline search + type filters, and a secondary "Timeline" view that
 * the advisor can toggle into.
 *
 * Records here are CONTEXTUAL VIEWS — never duplicated. The same signed
 * agreement appears in both the Client Dossier and the Experience
 * Dossier because both endpoints surface the same source row.
 */
import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FileText, ScrollText, FolderOpen, AlertCircle, CheckCircle2,
  Clock, ExternalLink, Search, ChevronDown, ChevronRight,
  Eye, EyeOff, Loader2, Send,
} from "lucide-react";
import {
  Surface, Heading, StatusTag, PortalButton, Empty, api, motion as portalMotion,
} from "../../../index";
import SendDossierModal from "./SendDossierModal";

const { fadeInUp, stagger } = portalMotion;

const KIND_META = {
  form:      { label: "Forms",      icon: FileText,   tone: "neutral" },
  agreement: { label: "Agreements", icon: ScrollText, tone: "gold" },
  document:  { label: "Documents",  icon: FolderOpen, tone: "sage" },
};

const STATUS_TONE = {
  signed:    "sage",
  submitted: "sage",
  completed: "sage",
  ready:     "sage",
  pending:   "gold",
  sent:      "gold",
  drafted:   "neutral",
  draft:     "neutral",
  not_sent:  "neutral",
};

const VISIBILITY_LABEL = {
  client:   { label: "Client-visible", icon: Eye },
  advisor:  { label: "Advisor-only",   icon: EyeOff },
  internal: { label: "Internal",       icon: EyeOff },
  archived: { label: "Archived",       icon: EyeOff },
};

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return null; }
}

function RecordCard({ record, onChangeVisibility }) {
  const meta = KIND_META[record.kind] || KIND_META.document;
  const KindIcon = meta.icon;
  const tone = STATUS_TONE[record.status] || "neutral";
  const vis = VISIBILITY_LABEL[record.visibility] || VISIBILITY_LABEL.advisor;
  const VisIcon = vis.icon;
  const completed = record.completed_at || record.signed_at;
  const [menuOpen, setMenuOpen] = React.useState(false);

  const sourceKind = record.source_record?.collection === "form_instances" ? "form_instance"
                   : record.source_record?.collection === "form_submissions" ? "form_submission"
                   : record.source_record?.collection === "trip_agreements" ? "agreement"
                   : "document";

  return (
    <div
      className="px-4 py-3.5 hover:bg-[hsl(var(--background-paper))]/60 transition-colors"
      data-testid={`dossier-record-${record.kind}-${record.id}`}
    >
      <div className="flex items-start gap-3">
        <KindIcon className="size-4 text-[hsl(var(--foreground-muted))] mt-1 shrink-0" strokeWidth={1.5} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusTag tone={tone}>{record.status?.replace(/_/g, " ")}</StatusTag>
            {record.subtype && (
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
                {String(record.subtype).replace(/_/g, " ")}
              </span>
            )}
            <div className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
                data-testid={`dossier-visibility-${record.kind}-${record.id}`}
              >
                <VisIcon className="size-3" strokeWidth={1.5} /> {vis.label}
              </button>
              {menuOpen && (
                <div
                  className="absolute z-20 mt-1 left-0 bg-white border border-[rgba(0,0,0,0.08)] rounded-md shadow-md py-1 min-w-[170px]"
                  data-testid={`dossier-visibility-menu-${record.kind}-${record.id}`}
                  onMouseLeave={() => setMenuOpen(false)}
                >
                  {["client", "advisor", "internal", "archived"].map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        onChangeVisibility?.(sourceKind, record.id, v);
                      }}
                      className={`w-full px-3 py-1.5 text-left font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-[hsl(var(--background-paper))] ${record.visibility === v ? "text-[hsl(var(--foreground))]" : "text-[hsl(var(--foreground-muted))]"}`}
                      data-testid={`dossier-visibility-set-${v}-${record.id}`}
                    >
                      {VISIBILITY_LABEL[v]?.label || v}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <p className="mt-1 font-serif text-[15px] text-[hsl(var(--foreground))] truncate">
            {record.title || "Untitled record"}
          </p>
          <div className="mt-1 flex items-center gap-3 font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">
            {completed && (
              <span className="inline-flex items-center gap-1">
                <CheckCircle2 className="size-3 text-[hsl(168,22%,40%)]" strokeWidth={1.5} />
                {fmtDate(completed)}
              </span>
            )}
            {!completed && record.attached_at && (
              <span className="inline-flex items-center gap-1">
                <Clock className="size-3" strokeWidth={1.5} />
                Attached {fmtDate(record.attached_at)}
              </span>
            )}
            {record.scope === "client_only" && (
              <span className="px-1.5 py-0.5 rounded bg-[hsl(var(--background-paper))] text-[10px] uppercase tracking-[0.18em]">
                Client-level
              </span>
            )}
          </div>
        </div>
        {record.actions?.open && (
          <a
            href={record.actions.open}
            target={record.actions.open?.startsWith("http") ? "_blank" : undefined}
            rel="noreferrer"
            className="text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] shrink-0"
            data-testid={`dossier-open-${record.kind}-${record.id}`}
          >
            <ExternalLink className="size-3.5" strokeWidth={1.5} />
          </a>
        )}
      </div>
    </div>
  );
}

function RecordSection({ kind, records, query, onChangeVisibility }) {
  const [collapsed, setCollapsed] = React.useState(false);
  const meta = KIND_META[kind];
  const filtered = records
    .filter((r) => r.kind === kind)
    .filter((r) => {
      if (!query) return true;
      const q = query.toLowerCase();
      return (
        (r.title || "").toLowerCase().includes(q) ||
        (r.subtype || "").toLowerCase().includes(q) ||
        (r.status || "").toLowerCase().includes(q)
      );
    });
  const Icon = meta.icon;
  return (
    <Surface variant="default" padded={false} data-testid={`dossier-section-${kind}`}>
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="w-full px-4 py-3 flex items-center justify-between border-b border-[rgba(0,0,0,0.05)] hover:bg-[hsl(var(--background-paper))]/40 transition-colors"
        data-testid={`dossier-section-toggle-${kind}`}
      >
        <div className="flex items-center gap-2">
          <Icon className="size-4 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
          <span className="font-serif text-[16px] text-[hsl(var(--foreground))]">{meta.label}</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
            · {filtered.length}
          </span>
        </div>
        {collapsed ? <ChevronRight className="size-4 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
                   : <ChevronDown  className="size-4 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />}
      </button>
      {!collapsed && (
        filtered.length === 0 ? (
          <div className="px-4 py-6 font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">
            No {meta.label.toLowerCase()} yet.
          </div>
        ) : (
          <ul className="divide-y divide-[rgba(0,0,0,0.04)]">
            {filtered.map((r) => (
              <li key={`${r.kind}-${r.id}`}>
                <RecordCard record={r} onChangeVisibility={onChangeVisibility} />
              </li>
            ))}
          </ul>
        )
      )}
    </Surface>
  );
}

function RequiredStrip({ required }) {
  const missing = (required || []).filter((r) => !r.present);
  if (!missing.length) {
    return (
      <Surface variant="paper" padded data-testid="dossier-required-strip">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="size-4 text-[hsl(168,22%,40%)]" strokeWidth={1.5} />
          <p className="font-serif text-[14.5px] text-[hsl(var(--foreground))]">
            All required records are in place.
          </p>
        </div>
      </Surface>
    );
  }
  return (
    <Surface variant="paper" padded data-testid="dossier-required-strip">
      <div className="flex items-start gap-3">
        <AlertCircle className="size-4 text-[hsl(35,75%,52%)] mt-1 shrink-0" strokeWidth={1.5} />
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">
            Missing · {missing.length}
          </p>
          <ul className="mt-1.5 flex items-center gap-3 flex-wrap">
            {missing.map((r) => (
              <li key={r.key} className="font-serif text-[14px] text-[hsl(var(--foreground))]" data-testid={`dossier-missing-${r.key}`}>
                {r.label}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Surface>
  );
}

function TimelineView({ records }) {
  if (!records?.length) {
    return <Empty title="No timeline events yet." icon={<Clock className="size-6" strokeWidth={1.25} />} />;
  }
  return (
    <Surface variant="default" padded={false} data-testid="dossier-timeline">
      <ul className="divide-y divide-[rgba(0,0,0,0.04)]">
        {records.map((r) => (
          <li key={`${r.kind}-${r.id}`} className="px-4 py-3 flex items-start gap-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] w-24 shrink-0 mt-1">
              {fmtDate(r.completed_at || r.attached_at) || "—"}
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-serif text-[14.5px] text-[hsl(var(--foreground))] truncate">{r.title}</p>
              <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">
                {KIND_META[r.kind]?.label || r.kind} · {(r.status || "").replace(/_/g, " ")}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </Surface>
  );
}

export default function DossierPanel({ trip, clientId, scope = "trip" }) {
  const tripId = trip?.id;
  const targetId = scope === "client" ? clientId : tripId;
  const [data, setData] = React.useState(null);
  const [audit, setAudit] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [query, setQuery] = React.useState("");
  const [view, setView] = React.useState("sections"); // "sections" | "timeline"

  const loadDossier = React.useCallback(() => {
    if (!targetId) return;
    setLoading(true);
    const path = scope === "client" ? `/dossier/client/${targetId}` : `/dossier/trip/${targetId}`;
    const auditParams = scope === "client" ? { client_id: targetId, limit: 8 } : { trip_id: targetId, limit: 8 };
    Promise.all([
      api.get(path),
      api.get(`/dossier/audit`, { params: auditParams }).catch(() => ({ data: { rows: [] } })),
    ]).then(([d, a]) => {
      setData(d.data);
      setAudit(a.data?.rows || []);
    }).catch(() => setData(null)).finally(() => setLoading(false));
  }, [targetId, scope]);

  React.useEffect(() => { loadDossier(); }, [loadDossier]);

  const handleChangeVisibility = React.useCallback(async (sourceKind, recordId, visibility) => {
    try {
      await api.patch(`/dossier/records/${sourceKind}/${recordId}/visibility`, { visibility });
      loadDossier();
    } catch {
      /* silent — toast handled elsewhere */
    }
  }, [loadDossier]);

  const [sendOpen, setSendOpen] = React.useState(false);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="dossier-loading">
        <Loader2 className="size-5 text-[hsl(var(--foreground-muted))] animate-spin" strokeWidth={1.5} />
      </div>
    );
  }

  if (!data) {
    return <Empty title="Dossier unavailable." hint="Try again in a moment." icon={<FolderOpen className="size-6" strokeWidth={1.25} />} />;
  }

  const { context, summary, required, records } = data;

  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-6" data-testid="dossier-panel">
      {/* Summary header */}
      <motion.div variants={fadeInUp}>
        <Surface variant="paper" padded>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent-gold))]">
                {scope === "client" ? "Client Dossier" : "Experience Dossier"}
                {context?.experience_code || context?.client_code
                  ? ` · ${context.experience_code || context.client_code}`
                  : ""}
              </p>
              <Heading level="h3" className="mt-1">{context?.title || "Dossier"}</Heading>
              <p className="mt-1 font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">
                {summary.total} records · {summary.completed} complete · {summary.pending} in motion
              </p>
            </div>
            <div className="flex items-center gap-1" data-testid="dossier-view-toggle">
              <PortalButton
                variant="ghost"
                size="sm"
                onClick={() => setSendOpen(true)}
                icon={<Send className="size-3 mr-1" strokeWidth={1.5} />}
                data-testid="dossier-send-to-client"
              >Send to client</PortalButton>
              <button
                type="button"
                onClick={() => setView("sections")}
                className={`px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${view === "sections" ? "bg-[hsl(var(--foreground))] text-white" : "bg-white border border-[rgba(0,0,0,0.06)] text-[hsl(var(--foreground-muted))]"}`}
                data-testid="dossier-view-sections"
              >Sections</button>
              <button
                type="button"
                onClick={() => setView("timeline")}
                className={`px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${view === "timeline" ? "bg-[hsl(var(--foreground))] text-white" : "bg-white border border-[rgba(0,0,0,0.06)] text-[hsl(var(--foreground-muted))]"}`}
                data-testid="dossier-view-timeline"
              >Timeline</button>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-[hsl(var(--foreground))]">
            <Counter label="Forms" value={summary.forms} icon={FileText} />
            <Counter label="Agreements" value={summary.agreements} icon={ScrollText} />
            <Counter label="Documents" value={summary.documents} icon={FolderOpen} />
            <Counter label="Pending" value={summary.pending} icon={Clock} />
          </div>
        </Surface>
      </motion.div>

      {/* Missing / required strip */}
      <motion.div variants={fadeInUp}>
        <RequiredStrip required={required} />
      </motion.div>

      {/* Search bar */}
      <motion.div variants={fadeInUp}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search this dossier — title, type, status…"
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-[rgba(0,0,0,0.08)] rounded-md font-serif text-[14px] outline-none focus:border-[hsl(var(--accent-gold))]/40"
            data-testid="dossier-search"
          />
        </div>
      </motion.div>

      {/* Sections or Timeline */}
      {view === "sections" ? (
        <motion.div variants={fadeInUp} className="space-y-3">
          <RecordSection kind="form" records={records} query={query} onChangeVisibility={handleChangeVisibility} />
          <RecordSection kind="agreement" records={records} query={query} onChangeVisibility={handleChangeVisibility} />
          <RecordSection kind="document" records={records} query={query} onChangeVisibility={handleChangeVisibility} />
        </motion.div>
      ) : (
        <motion.div variants={fadeInUp}>
          <TimelineView records={records} />
        </motion.div>
      )}

      {/* Phase 1.3 · Recent activity strip (dossier_audit) */}
      {audit.length > 0 && (
        <motion.div variants={fadeInUp}>
          <Surface variant="paper" padded data-testid="dossier-recent-activity">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-2">
              Recent activity · {audit.length}
            </p>
            <ul className="space-y-1.5 max-h-48 overflow-y-auto">
              {audit.map((a) => (
                <li
                  key={a.id}
                  className="flex items-baseline gap-2 font-accent italic text-[12.5px] text-[hsl(var(--foreground-secondary))]"
                  data-testid={`dossier-audit-${a.id}`}
                >
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] w-24 shrink-0 not-italic">
                    {fmtDate(a.created_at) || "—"}
                  </span>
                  <span className="min-w-0 truncate">
                    {(a.action || "").replace(/_/g, " ")} · {a.record_kind} · {a.actor}
                    {a.prev_value && a.new_value ? (
                      <> · {String(a.prev_value).slice(0,18)} → {String(a.new_value).slice(0,18)}</>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          </Surface>
        </motion.div>
      )}

      {/* Editorial footnote — clarifies the contextual-view paradigm */}
      <motion.div variants={fadeInUp}>
        <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))] text-center" data-testid="dossier-footnote">
          The Dossier is a contextual view, not a folder. Records live where they were created
          and surface here whenever they apply.
          {scope === "trip" && context?.client_id && (
            <>
              {" "}
              <Link to={`/portal/advisor/clients/${context.client_id}#dossier`} className="underline decoration-[hsl(var(--foreground-muted))]/40 underline-offset-2 hover:text-[hsl(var(--foreground))]">
                Open Client Dossier
              </Link>
              .
            </>
          )}
        </p>
      </motion.div>

      {sendOpen && (
        <SendDossierModal
          scope={scope}
          target={{
            id: targetId,
            client_id: context?.client_id,
            title: context?.title,
          }}
          onClose={() => { setSendOpen(false); loadDossier(); }}
        />
      )}
    </motion.div>
  );
}

function Counter({ label, value, icon: Icon }) {
  return (
    <div className="px-3 py-2 bg-white rounded-md border border-[rgba(0,0,0,0.05)]">
      <div className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
        <Icon className="size-3" strokeWidth={1.5} /> {label}
      </div>
      <div className="mt-0.5 font-serif text-[20px] text-[hsl(var(--foreground))]">{value}</div>
    </div>
  );
}
